import '../Integration/Integrations.scss'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { OAuth2AppCard } from '../../components/OAuth2App/OAuth2AppCard/OAuth2AppCard'
import { type ShortOAuth2App, useListOAuth2AppsQuery } from '../../store/auth/auth'
import { useOAuthApps } from '../Integration/useOAuthApps'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { ManagedAtEnterpriseLevelInfo } from '../../components/ManagedAtEnterpriseLevel/ManagedAtEnterpriseLevelInfo'
import { Upsell } from '../Plan/DeltaTrial/Upsell/Upsell'
import { DELTA_TRIAL_FEATURES } from '../Plan/DeltaTrial/DeltaTrialFeatures'
import { ScrollNavigation, type ScrollNavigationSection } from '../../components/NavigationWithSideBar/ScrollNavigation'
import { HowToUseAppsCard } from '../../components/OAuth2App/HowToUseAppsCard/HowToUseAppsCard'
import { BuildWithZocksBanner } from './components/BuildWithZocksBanner/BuildWithZocksBanner'

interface AppDirectoryCategory {
  id: string
  label: string
  title: string
  matches: (app: ShortOAuth2App) => boolean
}

const workFlowAppNames = ['Zapier']

const APP_DIRECTORY_CATEGORIES: AppDirectoryCategory[] = [
  { id: 'mcp', label: 'MCP', title: 'MCP', matches: (app) => app.category === 'mcp' },
  { id: 'workflow', label: 'Workflow', title: 'Workflow', matches: (app) => workFlowAppNames.includes(app.name) },
  { id: 'custom', label: 'Custom (private)', title: 'Custom', matches: (app) => app.category !== 'mcp' && (app.type === 'dev' || app.type === 'internal') },
  {
    id: 'partner',
    label: 'Partner',
    title: 'Partner',
    matches: (app) => app.category !== 'mcp' && app.type === 'external' && !workFlowAppNames.includes(app.name),
  },
]

const createAppSection = (category: AppDirectoryCategory, apps: ShortOAuth2App[]): ScrollNavigationSection => ({
  id: category.id,
  label: category.label,
  title: category.title,
  content: (
    <Container fluid className='px-0 pb-5'>
      <Row className='g-3'>
        {apps.map((app) => (
          <Col key={app.clientId} xs={12} sm={6} xxl={3} className='d-flex justify-content-center integration-settings__common-cards'>
            <OAuth2AppCard oAuth2App={app} />
          </Col>
        ))}
        {category.id === 'mcp' ? (
          <Col xs={12} sm={6} xxl={3} className='d-flex justify-content-center integration-settings__common-cards'>
            <HowToUseAppsCard />
          </Col>
        ) : null}
      </Row>
    </Container>
  ),
})

export const OAuth2AppDirectory: React.FC = () => {
  useDocumentTitle('Integrations')
  const navigate = useNavigate()

  const { hasQuota, isFetching: quotaIsFetching, quotaSuccess } = useZocksQuota()
  const oauth2Enabled = hasQuota('integration.oauth2Enabled.value')

  const accountId = useAppSelector(selectAccountId)
  const { data: oAuth2AppData, isLoading: oAuth2AppIsLoading } = useListOAuth2AppsQuery(
    { accountId },
    { refetchOnMountOrArgChange: true, skip: !oauth2Enabled },
  )
  const { isOAuthAppAllowed, isAppCreationEnabled, accountIsLoading } = useOAuthApps()

  const oAuth2Apps: ShortOAuth2App[] = useMemo(() => {
    return (oAuth2AppData?.data ?? []).filter((app) => isOAuthAppAllowed(app.id))
  }, [oAuth2AppData, isOAuthAppAllowed])

  const sections: ScrollNavigationSection[] = useMemo(() => {
    return APP_DIRECTORY_CATEGORIES.map((category) => ({ category, apps: oAuth2Apps.filter(category.matches) }))
      .filter(({ apps }) => apps.length > 0)
      .map(({ category, apps }) => createAppSection(category, apps))
  }, [oAuth2Apps])

  const handleAddNewApp = useCallback(() => {
    navigate('/account/integration-settings/oauth2apps/new')
  }, [navigate])

  if (quotaIsFetching || oAuth2AppIsLoading || accountIsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (quotaSuccess && !oauth2Enabled) {
    return <Upsell feature={DELTA_TRIAL_FEATURES['appDirectory']} />
  }

  if (!oAuth2Apps.length) {
    return (
      <div>
        <ManagedAtEnterpriseLevelInfo resourceName={'Apps'} />
        {isAppCreationEnabled ? (
          <div className='integration-settings__integrations-container mt-4'>
            <BuildWithZocksBanner onAddNewApp={handleAddNewApp} />
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <ScrollNavigation
      sections={sections}
      defaultActiveSectionId={sections[0]?.id ?? ''}
      sectionClassName='integration-settings__integrations-container'
      contentFooter={
        isAppCreationEnabled ? (
          <div className='integration-settings__integrations-container'>
            <BuildWithZocksBanner onAddNewApp={handleAddNewApp} />
          </div>
        ) : null
      }
    />
  )
}
